import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Client, Databases, Storage } from "appwrite";

// Initialize Appwrite client
const client = new Client();
client
  .setEndpoint("https://cloud.appwrite.io/v1")
  .setProject("67fbee6a001260770104");

const databases = new Databases(client);
const storage = new Storage(client);

const DATABASE_ID = "68098bc80034d68e4e64";
const COLLECTION_ID = "68098bd900226cbeb1cd";
const BUCKET_ID = "680990d6000f00f9d400";

const PoemPage = () => {
  const { poemId } = useParams();
  const navigate = useNavigate();
  const [poem, setPoem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPoem = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await databases.getDocument(
          DATABASE_ID,
          COLLECTION_ID,
          poemId
        );

        // Check if poem is approved and is actually a poem
        if (!response.isApproved) {
          setError("This poem is not available.");
          return;
        }

        if (response.contentType && response.contentType.toLowerCase() !== "poem") {
          setError("Content not found.");
          return;
        }

        setPoem(response);
      } catch (error) {
        console.error("Error fetching poem:", error);
        setError("Poem not found.");
      } finally {
        setLoading(false);
      }
    };

    if (poemId) {
      fetchPoem();
    }
  }, [poemId]);

  const getImageUrl = (imageId) => {
    if (!imageId) return null;
    try {
      return storage.getFileView(BUCKET_ID, imageId);
    } catch (error) {
      console.error("Error generating image URL:", error);
      return "";
    }
  };

  const handleShare = () => {
    const shareData = {
      title: `${poem.title} - PoemXtra`,
      text: `Check out this beautiful poem: "${poem.title}"`,
      url: window.location.href
    };

    if (navigator.share) {
      navigator.share(shareData).catch(console.error);
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Link copied to clipboard!');
      }).catch((err) => {
        console.error('Failed to copy: ', err);
      });
    }
  };

useEffect(() => {
    // Wait for component to mount and DOM to be ready
    const initializeEzoicAds = () => {
      if (window.ezstandalone && window.ezstandalone.cmd) {
        window.ezstandalone.cmd.push(function () {
          // Pass ALL placement IDs in a single call (as per Ezoic docs)
          window.ezstandalone.showAds(172, 173);
        });
      }
    };

    // Small delay to ensure DOM elements are rendered
    const timer = setTimeout(initializeEzoicAds, 300);
    
    return () => clearTimeout(timer);
  }, []);



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading poem...</p>
        </div>
      </div>
    );
  }

  if (error || !poem) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <div className="text-6xl mb-4">😔</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h1>
          <p className="text-gray-600 mb-6">{error || "Poem not found."}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-blue-500 hover:text-blue-600 transition-colors"
          >
            <span>←</span>
            Back to PoemXtra
          </button>
        </div>

        {/* Poem Content */}
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Image */}
          {poem.imageId && (
            <div className="w-full h-64 md:h-80 bg-gray-100 flex items-center justify-center">
              <img
                src={getImageUrl(poem.imageId)}
                alt={poem.title}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Content */}
          <div className="p-6 md:p-8">
            <header className="mb-6">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                {poem.title}
              </h1>
               <div id="ezoic-pub-ad-placeholder-173"></div>
              {poem.author && (
                <p className="text-lg text-gray-600">by {poem.author}</p>
              )}
              <div className="text-sm text-gray-500 mt-2">
                Published on {new Date(poem.$createdAt).toLocaleDateString()}
              </div>
            </header>

            <div className="prose prose-lg max-w-none">
               <div id="ezoic-pub-ad-placeholder-172"></div>
              <pre className="whitespace-pre-wrap font-serif text-gray-700 leading-relaxed text-lg">
                {poem.content}
              </pre>
            </div>

            {/* Tags if available */}
            {poem.tags && poem.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {poem.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">Share this poem:</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const text = `Check out "${poem.title}" on PoemXtra: ${window.location.href}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors text-sm"
                >
                  WhatsApp
                </button>
                <button
                  onClick={() => {
                    const text = `"${poem.title}" - A beautiful poem on PoemXtra`;
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`, '_blank');
                  }}
                  className="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500 transition-colors text-sm"
                >
                  Twitter
                </button>
                <button
                  onClick={() => {
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
                >
                  Facebook
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard
                      .writeText(window.location.href)
                      .then(() => {
                        alert("Link copied to clipboard!");
                      })
                      .catch((err) => {
                        console.error("Failed to copy: ", err);
                      });
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors text-sm"
                >
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* SEO Meta tags would go in the head - you can add these with React Helmet */}
      </div>
    </div>
  );
};

export default PoemPage;